import React from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, Clock, Instagram, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import Seo from "../seo";
import { CLINIC, GOOGLE_REVIEW_URL } from "../api";
import { Reveal, Chapter } from "../components/motion";
import { useLanguage } from "../i18n";

export default function Contact() {
    const { t } = useLanguage();
    return (
        <div className="mx-auto max-w-7xl px-5 py-14 lg:px-10 lg:py-20">
            <Seo title="Contact | Dr. Krishnakanth AVB, Consultant Urologist Madurai"
                description={`Contact Dr. Krishnakanth AVB, Consultant Urologist at ${CLINIC.hospital}, ${CLINIC.address}. Phone ${CLINIC.phoneDisplay}. OPD ${CLINIC.opd}.`} />
            <Chapter index={1} label="Contact" />
            <Reveal>
                <h1 className="mt-8 max-w-2xl font-display text-4xl font-medium leading-tight text-navy sm:text-5xl">
                    Reach the clinic
                </h1>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-steel md:text-lg">
                    Consultations are at {CLINIC.hospital}, Madurai. Appointments are recommended.
                </p>
            </Reveal>

            <div className="mt-14 grid gap-12 lg:grid-cols-2 lg:gap-20">
                <Reveal className="space-y-8">
                    <div>
                        <h2 className="flex items-center gap-2 font-display text-xl font-medium text-navy"><MapPin className="h-5 w-5 text-accent" /> Hospital location</h2>
                        <p className="mt-3 text-sm font-medium leading-relaxed text-navy">{CLINIC.hospital}</p>
                        <p className="mt-1 text-sm leading-relaxed text-steel" data-testid="contact-address">{CLINIC.address}</p>
                        <a href={CLINIC.mapsLink} target="_blank" rel="noopener noreferrer" data-testid="get-directions-link"
                            className="mt-2 inline-block text-sm font-semibold text-accent hover:underline">
                            Get directions
                        </a>
                        <span className="mx-2 text-slate-300">·</span>
                        <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="leave-review-contact-link"
                            className="mt-2 inline-block text-sm font-semibold text-accent hover:underline">
                            Write a Google review
                        </a>
                    </div>
                    <div>
                        <h2 className="flex items-center gap-2 font-display text-xl font-medium text-navy"><Clock className="h-5 w-5 text-accent" /> OPD timings</h2>
                        <ul className="mt-4 overflow-hidden rounded-xl border border-border bg-white" data-testid="contact-opd-timings">
                            {CLINIC.opdSchedule.map((d) => (
                                <li key={d.day} className="flex items-center justify-between border-b border-border px-5 py-2.5 text-sm last:border-b-0">
                                    <span className="font-medium text-navy">{d.day}</span>
                                    <span className={d.hours.includes("Closed") ? "text-slate-400" : "text-steel"}>{d.hours}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700" data-testid="contact-emergency-note">
                            <Phone className="h-4 w-4" aria-hidden="true" />
                            {CLINIC.emergency} — call {CLINIC.phoneDisplay}
                        </p>
                    </div>
                    <div>
                        <h2 className="flex items-center gap-2 font-display text-xl font-medium text-navy"><Phone className="h-5 w-5 text-accent" /> Phone & WhatsApp</h2>
                        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                            <a href={CLINIC.phoneHref} data-testid="contact-call-btn"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-accent">
                                <Phone className="h-4 w-4" aria-hidden="true" />
                                {CLINIC.phoneDisplay}
                            </a>
                            <a href={CLINIC.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/20 px-6 py-3 text-sm font-semibold text-navy transition-all duration-300 hover:border-accent hover:text-accent">
                                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                                WhatsApp
                            </a>
                        </div>
                    </div>
                    <div>
                        <h2 className="flex items-center gap-2 font-display text-xl font-medium text-navy"><Instagram className="h-5 w-5 text-accent" /> {t("follow")}</h2>
                        <a href={CLINIC.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link"
                            className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-accent hover:underline">
                            @uro_bytes on Instagram
                        </a>
                    </div>
                    <div>
                        <h2 className="flex items-center gap-2 font-display text-xl font-medium text-navy"><Mail className="h-5 w-5 text-accent" /> Email</h2>
                        <a href={`mailto:${CLINIC.email}`} data-testid="contact-email-link"
                            className="mt-3 inline-block text-sm font-semibold text-accent hover:underline">
                            {CLINIC.email}
                        </a>
                    </div>
                    <Link to="/appointment" data-testid="contact-book-appointment-btn"
                        className="inline-flex items-center gap-2 rounded-full bg-navy px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-accent hover:shadow-lg">
                        <CalendarCheck className="h-4 w-4" aria-hidden="true" />
                        {t("book_appointment")}
                    </Link>
                </Reveal>
                <Reveal delay={0.1}>
                    <div className="overflow-hidden rounded-2xl border border-border" data-testid="map-section">
                        <iframe
                            title={`Map — ${CLINIC.hospital}, Madurai`}
                            src={CLINIC.mapsEmbed}
                            className="h-[480px] w-full border-0 lg:h-[560px]"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            allowFullScreen
                            data-testid="google-map-embed"
                        />
                    </div>
                    <p className="mt-3 text-xs text-slate-400">
                        Map: {CLINIC.hospital}, Uthangudi, Madurai. Use "Get directions" for turn-by-turn navigation.
                    </p>
                </Reveal>
            </div>
        </div>
    );
}
